import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import apiClient from "../../api/apiClient"
import { API_ENDPOINTS } from "../../constants/apiEndpoints"

// Initial state
const initialState = {
  items: [],
  totalQuantity: 0,
  totalPrice: 0,
  loading: false,
  error: null,
}

// Helper function to calculate totals
const calculateTotals = (items) => {
  const totalQuantity = items.reduce((total, item) => total + (item.quantity || 0), 0)
  const totalPrice = items.reduce((total, item) => {
    const price = item.productId?.price || item.price || 0
    return total + (price * (item.quantity || 0))
  }, 0)
  return { totalQuantity, totalPrice }
}

// Async thunks for cart operations
export const fetchCart = createAsyncThunk("cart/fetchCart", async (_, { rejectWithValue }) => {
  try {
    const response = await apiClient.get(API_ENDPOINTS.GET_CART)
    return response.data
  } catch (error) {
    return rejectWithValue(error.response?.data || "Failed to fetch cart")
  }
})

export const addToCart = createAsyncThunk(
  "cart/addToCart",
  async ({ productId, quantity = 1 }, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(API_ENDPOINTS.ADD_TO_CART, {
        productId,
        quantity,
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to add item to cart")
    }
  },
)

export const updateCartItem = createAsyncThunk(
  "cart/updateCartItem",
  async ({ productId, quantity }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(API_ENDPOINTS.UPDATE_CART, {
        productId,
        quantity,
      })
      return response.data
    } catch (error) {
      return rejectWithValue(error.response?.data || "Failed to update cart item")
    }
  },
)

export const removeFromCart = createAsyncThunk("cart/removeFromCart", async (productId, { rejectWithValue }) => {
  try {
    const response = await apiClient.delete(`${API_ENDPOINTS.REMOVE_FROM_CART}/${productId}`)
    return { productId, ...response.data }
  } catch (error) {
    return rejectWithValue(error.response?.data || "Failed to remove item from cart")
  }
})

// Cart slice
const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    clearCart: (state) => {
      state.items = []
      state.totalQuantity = 0
      state.totalPrice = 0
    },
    // Add a reducer for optimistic updates
    incrementQuantity: (state, action) => {
      const { productId } = action.payload
      const itemIndex = state.items.findIndex(
        (item) => item.productId === productId || item.productId._id === productId
      )
      
      if (itemIndex >= 0) {
        state.items[itemIndex].quantity += 1
        const { totalQuantity, totalPrice } = calculateTotals(state.items)
        state.totalQuantity = totalQuantity
        state.totalPrice = totalPrice
      }
    },
    decrementQuantity: (state, action) => {
      const { productId } = action.payload
      const itemIndex = state.items.findIndex(
        (item) => item.productId === productId || item.productId._id === productId
      )
      
      if (itemIndex >= 0) {
        if (state.items[itemIndex].quantity > 1) {
          state.items[itemIndex].quantity -= 1
        } else {
          state.items.splice(itemIndex, 1)
        }
        const { totalQuantity, totalPrice } = calculateTotals(state.items)
        state.totalQuantity = totalQuantity
        state.totalPrice = totalPrice
      }
    },
    addItem: (state, action) => {
      const { productId } = action.payload
      const itemIndex = state.items.findIndex(
        (item) => item.productId === productId || item.productId._id === productId
      )
      
      if (itemIndex >= 0) {
        state.items[itemIndex].quantity += 1
      } else {
        state.items.push({
          productId,
          quantity: 1
        })
      }
      const { totalQuantity, totalPrice } = calculateTotals(state.items)
      state.totalQuantity = totalQuantity
      state.totalPrice = totalPrice
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch cart
      .addCase(fetchCart.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCart.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items || []
        const { totalQuantity, totalPrice } = calculateTotals(state.items)
        state.totalQuantity = totalQuantity
        state.totalPrice = totalPrice
      })
      .addCase(fetchCart.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || "Failed to fetch cart"
      })

      // Add to cart
      .addCase(addToCart.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addToCart.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items || []
        const { totalQuantity, totalPrice } = calculateTotals(state.items)
        state.totalQuantity = totalQuantity
        state.totalPrice = totalPrice
      })
      .addCase(addToCart.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || "Failed to add item to cart"
      })

      // Update cart item
      .addCase(updateCartItem.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCartItem.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items || []
        const { totalQuantity, totalPrice } = calculateTotals(state.items)
        state.totalQuantity = totalQuantity
        state.totalPrice = totalPrice
      })
      .addCase(updateCartItem.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || "Failed to update cart item"
      })

      // Remove from cart
      .addCase(removeFromCart.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(removeFromCart.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload.items || []
        const { totalQuantity, totalPrice } = calculateTotals(state.items)
        state.totalQuantity = totalQuantity
        state.totalPrice = totalPrice
      })
      .addCase(removeFromCart.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload || "Failed to remove item from cart"
      })
  },
})

// Export actions and reducer
export const { clearCart, incrementQuantity, decrementQuantity, addItem } = cartSlice.actions
export default cartSlice.reducer

// Selectors
export const selectCartItems = (state) => state.cart.items || []
export const selectCartTotalQuantity = (state) => state.cart.totalQuantity
export const selectCartTotalPrice = (state) => state.cart.totalPrice
export const selectCartLoading = (state) => state.cart.loading
export const selectCartError = (state) => state.cart.error
export const selectCartItemQuantity = (state, productId) => {
  if (!state.cart.items || !state.cart.items.length) return 0

  const item = state.cart.items.find(
    (item) =>
      (item.productId && item.productId._id === productId) ||
      (typeof item.productId === "string" && item.productId === productId),
  )

  return item ? item.quantity : 0
}